// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { addAssetURLLink } from '@/asset/api/add-asset-url-link';
import { deleteAssetFile } from '@/asset/api/delete-asset-file';
import { getAssetFiles } from '@/asset/api/get-asset-files';
import { getAssetURLLinks } from '@/asset/api/get-asset-url-links';
import { uploadAssetFile } from '@/asset/api/upload-asset-file';
import { AssetErrorBoundary } from '@/asset/components/asset-error-boundary';
import { useAsset } from '@/asset/hooks/use-asset';
import { DeleteOwnerAttachmentDialog } from '@/components/attachment-delete-dialog';
import { AttachmentsTable } from '@/components/attachments-table';
import {
  FileInputButton,
  FileInputButtonHandle,
} from '@/components/file-input-button';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { LinkDialog } from '@/components/link-dialog';
import { LinkInputButton } from '@/components/link-input-button';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UploadErrorDialog } from '@/components/upload-error-dialog';
import { UploadFileOutlined } from '@/constants/hardfin-standard-icons';
import { isFileReadError } from '@/file/api/is-file-read-error';
import { File, combineAsAttachments } from '@/file/api/types';
import { useDisclosure } from '@/hooks/use-disclosure';
import { deleteURLLink } from '@/url-link/api/delete-url-link';
import { AddURLLinkParams, URLLink } from '@/url-link/api/types';
import {
  UpdateURLLinkParams,
  updateURLLink,
} from '@/url-link/api/update-url-link';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';

export const Route = createFileRoute('/_app/assets/$assetKey/attachments')({
  component: AssetAttachmentsRoute,
});

function AssetAttachmentsRoute() {
  const { assetKey } = Route.useParams();

  return (
    <AssetErrorBoundary>
      <AssetAttachmentsContent assetKey={assetKey} />
    </AssetErrorBoundary>
  );
}

function AssetAttachmentsContent(props: { assetKey: string }) {
  const queryClient = useQueryClient();
  const { asset } = useAsset(props.assetKey);
  const fileInputRef = useRef<FileInputButtonHandle>(null);
  const [deletingFile, setDeletingFile] = useState<File | null>(null);
  const [deletingURLLink, setDeletingURLLink] = useState<URLLink | null>(null);
  const [editingURLLink, setEditingURLLink] = useState<URLLink | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const deleteFileDialog = useDisclosure();
  const deleteURLLinkDialog = useDisclosure();
  const editURLLinkDialog = useDisclosure();
  const uploadErrorDialog = useDisclosure();

  const filesQuery = useSuspenseQuery({
    queryKey: ['asset', props.assetKey, 'file'],
    queryFn: () => getAssetFiles(props.assetKey),
  });

  const urlLinksQuery = useSuspenseQuery({
    queryKey: ['asset', props.assetKey, 'url-link'],
    queryFn: () => getAssetURLLinks(props.assetKey),
  });

  const uploadFileMutation = useMutation({
    mutationFn: (file: globalThis.File) => uploadAssetFile(asset.id, file),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] }),
    onError: (error) => {
      if (isFileReadError(error)) {
        uploadErrorDialog.open();
      }
    },
  });

  const addURLLinkMutation = useMutation({
    mutationFn: (params: AddURLLinkParams) => addAssetURLLink(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (file: File) => deleteAssetFile(props.assetKey, file.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
      deleteFileDialog.close();
      setDeletingFile(null);
    },
  });

  const deleteURLLinkMutation = useMutation({
    mutationFn: (urlLink: URLLink) => deleteURLLink(urlLink.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
      deleteURLLinkDialog.close();
      setDeletingURLLink(null);
    },
  });

  const updateURLLinkMutation = useMutation({
    mutationFn: (params: UpdateURLLinkParams) => updateURLLink(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
      editURLLinkDialog.close();
      setEditingURLLink(null);
    },
  });

  const handleDeleteFile = (file: File) => {
    setDeletingFile(file);
    deleteFileDialog.open();
  };

  const handleDeleteURLLink = (urlLink: URLLink) => {
    setDeletingURLLink(urlLink);
    deleteURLLinkDialog.open();
  };

  const handleEditURLLink = (urlLink: URLLink) => {
    setEditingURLLink(urlLink);
    editURLLinkDialog.open();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const confirmDeleteFile = () => {
    if (deletingFile) {
      deleteFileMutation.mutate(deletingFile);
    }
  };

  const confirmDeleteURLLink = () => {
    if (deletingURLLink) {
      deleteURLLinkMutation.mutate(deletingURLLink);
    }
  };

  const handleUpdateURLLink = (params: UpdateURLLinkParams) => {
    updateURLLinkMutation.mutate(params);
  };

  // Combine attachments for filtering
  const allAttachments = combineAsAttachments(
    filesQuery.data.data,
    urlLinksQuery.data.data
  );
  const filteredAttachments = (() => {
    if (selectedTab === 0) return allAttachments; // All
    if (selectedTab === 1)
      return allAttachments.filter((attachment) => attachment.type === 'file'); // Files
    if (selectedTab === 2)
      return allAttachments.filter((attachment) => attachment.type === 'link'); // Links
    return allAttachments;
  })();

  return (
    <Stack>
      {/* Error Snackbars */}
      <SupportSnackbar
        isOpen={deleteFileMutation.isError}
        onClose={deleteFileMutation.reset}
        message="An error occurred while deleting the file"
      />
      <SupportSnackbar
        isOpen={deleteURLLinkMutation.isError}
        onClose={deleteURLLinkMutation.reset}
        message="An error occurred while deleting the link"
      />
      <SupportSnackbar
        isOpen={updateURLLinkMutation.isError}
        onClose={updateURLLinkMutation.reset}
        message="An error occurred while updating the link"
      />
      <UploadErrorDialog
        isOpen={uploadErrorDialog.isOpen}
        onClose={uploadErrorDialog.close}
        onRetry={() => {
          uploadErrorDialog.close();
          fileInputRef.current?.openFilePicker();
        }}
      />

      <DeleteOwnerAttachmentDialog
        attachmentType="file"
        includeAuditNote
        isPending={deleteFileMutation.isPending}
        isOpen={deleteFileDialog.isOpen}
        onClose={deleteFileDialog.close}
        onConfirm={confirmDeleteFile}
        ownerType="asset"
      />
      <DeleteOwnerAttachmentDialog
        attachmentType="link"
        includeAuditNote
        isPending={deleteURLLinkMutation.isPending}
        isOpen={deleteURLLinkDialog.isOpen}
        onClose={deleteURLLinkDialog.close}
        onConfirm={confirmDeleteURLLink}
        ownerType="asset"
      />

      {/* Edit URL Link Dialog */}
      <LinkDialog
        open={editURLLinkDialog.isOpen}
        onClose={editURLLinkDialog.close}
        editLink={editingURLLink}
        onAddLink={() => {
          // This won't be called in edit mode, but required for the interface
        }}
        onEditLink={handleUpdateURLLink}
      />

      <HardfinTabCardWrapper
        title="Files and links"
        actions={
          <Stack direction="row" spacing={1}>
            <FileInputButton
              ref={fileInputRef}
              size="small"
              onChange={uploadFileMutation.mutate}
              disabled={uploadFileMutation.isPending}
              startIcon={<UploadFileOutlined fontSize="small" />}
            >
              Upload a file
            </FileInputButton>
            <LinkInputButton
              size="small"
              onAddLink={(name, url) => {
                addURLLinkMutation.mutate({
                  entityKey: props.assetKey,
                  name,
                  url,
                });
              }}
              disabled={addURLLinkMutation.isPending}
            />
          </Stack>
        }
      >
        <Stack spacing={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingLeft: 1,
              '& .Mui-selected': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiTab-root': {
                px: 2,
              },
              '& .MuiTab-root.MuiButtonBase-root': {
                ml: 0,
              },
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Asset attachment filters"
            >
              <Tab label={`All (${allAttachments.length})`} />
              <Tab label={`Files (${filesQuery.data.data.length})`} />
              <Tab label={`Links (${urlLinksQuery.data.data.length})`} />
            </Tabs>
          </Box>
          <AttachmentsTable
            files={filesQuery.data.data}
            urlLinks={urlLinksQuery.data.data}
            filteredAttachments={filteredAttachments}
            onDeleteFile={handleDeleteFile}
            onDeleteURLLink={handleDeleteURLLink}
            onEditURLLink={handleEditURLLink}
          />
        </Stack>
      </HardfinTabCardWrapper>
    </Stack>
  );
}
